import { ThemedText } from "@/components/themed-text";
import { BrandColors } from "@/constants/theme";
import { money } from "@/lib/utils";
import Ionicons from "@react-native-vector-icons/ionicons";
import React from "react";
import {
  ActivityIndicator,
  Modal,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";

interface QuotePriceModalProps {
  visible: boolean;
  minimumEarnings: number;
  quoteEarnings: string;
  loading?: boolean;
  onClose: () => void;
  onChangeEarnings: (amount: string) => void;
  onSubmit: () => void;
}

const INCREMENT = 5;

export function QuotePriceModal({
  visible,
  minimumEarnings,
  quoteEarnings,
  loading = false,
  onClose,
  onChangeEarnings,
  onSubmit,
}: QuotePriceModalProps) {
  const currentEarnings = parseFloat(quoteEarnings) || minimumEarnings;
  const canDecrement = currentEarnings > minimumEarnings;

  const handleIncrement = (delta: number) => {
    const next = Math.max(minimumEarnings, currentEarnings + delta);
    onChangeEarnings(next.toFixed(2));
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.card}>
          <View style={styles.header}>
            <View style={styles.headerText}>
              <ThemedText style={styles.title} type="defaultSemiBold">
                Your Quote
              </ThemedText>
              <ThemedText style={styles.subtitle} type="small">
                Minimum earnings{" "}
                <ThemedText style={styles.minimumPrice} type="smallSemiBold">
                  {money(minimumEarnings)}
                </ThemedText>
              </ThemedText>
            </View>
            <TouchableOpacity
              style={styles.closeButton}
              onPress={onClose}
              hitSlop={8}
              activeOpacity={0.7}
            >
              <Ionicons name="close" size={18} color={BrandColors.charcoal} />
            </TouchableOpacity>
          </View>

          <View style={styles.stepper}>
            <View style={styles.stepperControls}>
              <TouchableOpacity
                style={[
                  styles.stepperButton,
                  !canDecrement && styles.stepperButtonDisabled,
                ]}
                onPress={() => handleIncrement(-INCREMENT)}
                disabled={!canDecrement}
                activeOpacity={0.7}
              >
                <Ionicons
                  name="remove"
                  size={22}
                  color={
                    canDecrement ? BrandColors.charcoal : BrandColors.lightGray
                  }
                />
              </TouchableOpacity>

              <ThemedText style={styles.priceDisplay} type="defaultSemiBold">
                {money(currentEarnings)}
              </ThemedText>

              <TouchableOpacity
                style={styles.stepperButton}
                onPress={() => handleIncrement(INCREMENT)}
                activeOpacity={0.7}
              >
                <Ionicons name="add" size={22} color={BrandColors.charcoal} />
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.actions}>
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={onClose}
              activeOpacity={0.7}
            >
              <ThemedText style={styles.cancelButtonText} type="smallSemiBold">
                Cancel
              </ThemedText>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.submitButton}
              onPress={onSubmit}
              disabled={loading}
              activeOpacity={0.8}
            >
              {loading ? (
                <ActivityIndicator color={BrandColors.white} />
              ) : (
                <ThemedText
                  style={styles.submitButtonText}
                  type="smallSemiBold"
                >
                  Submit Quote
                </ThemedText>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.45)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  card: {
    width: "100%",
    backgroundColor: BrandColors.offWhite,
    borderRadius: 20,
    padding: 24,
    gap: 24,
  },
  header: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  headerText: {
    flex: 1,
    gap: 6,
  },
  title: {
    color: BrandColors.charcoal,
    fontSize: 22,
    fontWeight: "700",
  },
  subtitle: {
    color: BrandColors.mediumGray,
  },
  minimumPrice: {
    color: BrandColors.primary,
    fontWeight: "700",
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: BrandColors.backgroundGray,
    alignItems: "center",
    justifyContent: "center",
  },
  stepper: {
    borderWidth: 1,
    borderColor: BrandColors.lightGray,
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 20,
  },
  stepperControls: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  stepperButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: BrandColors.backgroundGray,
    alignItems: "center",
    justifyContent: "center",
  },
  stepperButtonDisabled: {
    backgroundColor: BrandColors.offWhite,
  },
  priceDisplay: {
    fontSize: 32,
    lineHeight: 38,
    fontWeight: "700",
    color: BrandColors.secondary,
  },
  actions: {
    flexDirection: "row",
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 14,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: BrandColors.lightGray,
    backgroundColor: BrandColors.white,
  },
  cancelButtonText: {
    color: BrandColors.charcoal,
    fontSize: 15,
    fontWeight: "600",
  },
  submitButton: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 14,
    borderRadius: 14,
    backgroundColor: BrandColors.primary,
  },
  submitButtonText: {
    color: BrandColors.white,
    fontSize: 15,
    fontWeight: "600",
  },
});
